import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { renameKeysFromSDKRequestToRESTRequest, renameKeysFromRESTResponseToSDKResponse, } from '@wix/sdk-runtime/rename-all-nested-keys';
import * as ambassadorWixBookingsAvailabilityV1SlotAvailability from './bookings-availability-v1-slot-availability-availability-calendar.http';
export var LocationType;
(function (LocationType) {
    LocationType["UNDEFINED"] = "UNDEFINED";
    LocationType["OWNER_BUSINESS"] = "OWNER_BUSINESS";
    LocationType["OWNER_CUSTOM"] = "OWNER_CUSTOM";
    LocationType["CUSTOM"] = "CUSTOM";
})(LocationType || (LocationType = {}));
export var SortOrder;
(function (SortOrder) {
    SortOrder["ASC"] = "ASC";
    SortOrder["DESC"] = "DESC";
})(SortOrder || (SortOrder = {}));
export var LocationLocationType;
(function (LocationLocationType) {
    LocationLocationType["UNKNOWN_LOCATION_TYPE"] = "UNKNOWN_LOCATION_TYPE";
    /** A business location, either the default business address, or locations defined for the business by the Business Info. */
    LocationLocationType["BUSINESS"] = "BUSINESS";
    /** The location is unique to this service and isn't defined as one of the business locations. */
    LocationLocationType["CUSTOM"] = "CUSTOM";
    /** The location can be determined by the customer and is not set up beforehand. */
    LocationLocationType["CUSTOMER"] = "CUSTOMER";
})(LocationLocationType || (LocationLocationType = {}));
/**
 * Retrieves the availability for sessions
 * that match the provided query criteria (paging, filtering, and sorting).
 *
 *
 * The Availability Calendar APIs calculate the availability of sessions for
 * appointments and classes, but not
 * for courses.
 *
 * The entire list of slots is returned in case you want to display both
 * available and non-available slots in the calendar for your customers.
 * Using the `bookable` property, you can limit the display to available
 * slots only.
 *
 * When querying, you must enter a start date and an end date. This avoids very large
 * results that can impact performance.
 *
 * #### Calculating availability
 *
 * The availability is determined
 * by checking:
 * + **The sessions' open slots**. A slot is considered open if the session's
 * capacity is greater than number of participants.
 * + **Booking policies**. Policies that affect whether a slot is considered
 * available include `tooEarlyToBook`, `tooLateToBook`, and `bookOnlineDisabled`.
 *
 * Locked sessions do not impact session availability and `bookable` can
 * be `true` even if `locked` is `true`. For example, if a session has a waitlist and a
 * place frees up, the slot is offered to the customers on the waitlist, one by one. The
 * session remains locked because there is still a waitlist, but for a period of time
 * there is availability, until a customer on the waitlist takes the slot. Locking
 * prevents customers who are not yet on the waitlist from grabbing the slot.
 *
 * #### Handling Daylight Savings Time (DST) for local time zones
 *
 * Because of DST, there are cases where certain times either do not exist
 * or exist twice for a local time zone.
 * For example, the tine `00:05` on September 5th 2021 might not exist in Santiago, Chile,
 * because at `00:00` the clock moved
 * 1 hour forward to `01:00`.
 *
 * In this case, the Availability Calendar APIs take this
 * into account and mediate the time gaps automatically. The non-existent local time is
 * automatically moved forward 1 hour to match local DST. Local times that exist do not change.
 * So if the `queryAvailability()` function is called with a `startDate` of `2021-09-05T00:00:01.000`
 * and an `endDate` of `2021-09-06T00:00:02.000`, `2021-09-05T01:00:01.000` is used in the query
 * instead. The start time shifts one hour forward and the end time remains the same.
 * @param query - Query options.
 * @public
 * @documentationMaturity preview
 * @requiredField query
 * @requiredField query.filter
 * @param options - Additional options for performing the query.
 * @permissionId BOOKINGS.AVAILABILITY_READ
 * @permissionScope Read Bookings - Public Data
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-PUBLIC
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Read Bookings - Including Participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-SENSITIVE
 * @permissionScope Read Bookings - all read permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.READ-BOOKINGS
 * @permissionScope Read Bookings Calendar Availability
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-CALENDAR
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @applicableIdentity APP
 * @applicableIdentity VISITOR
 */
export async function queryAvailability(query, options) {
    // @ts-ignore
    const { httpClient, sideEffects } = arguments[2];
    const payload = renameKeysFromSDKRequestToRESTRequest({
        query: query,
        timezone: options?.timezone,
        slotsPerDay: options?.slotsPerDay,
    });
    const reqOpts = ambassadorWixBookingsAvailabilityV1SlotAvailability.queryAvailability(payload);
    sideEffects?.onSiteCall?.();
    try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return renameKeysFromRESTResponseToSDKResponse(result.data);
    }
    catch (err) {
        const transformedError = sdkTransformError(err, {
            spreadPathsToArguments: {},
            explicitPathsToArguments: {
                query: '$[0]',
                timezone: '$[1].timezone',
                slotsPerDay: '$[1].slotsPerDay',
            },
            singleArgumentUnchanged: false,
        }, ['query', 'options']);
        sideEffects?.onError?.(err);
        throw transformedError;
    }
}
/**
 * Returns availability for a given schedule ID
 * The availability for a course is calculated by:
 * - Checking for total spots by the schedule's capacity
 * - Checking for open spots by subtracting the current number of participants from the total spots
 * current number of participants is calculated by summing the number of participants of all bookings booked to the schedule
 * @param scheduleId - The schedule ID for which availability is being checked.
 * @public
 * @documentationMaturity preview
 * @requiredField scheduleId
 */
export async function getScheduleAvailability(scheduleId) {
    // @ts-ignore
    const { httpClient, sideEffects } = arguments[1];
    const payload = renameKeysFromSDKRequestToRESTRequest({
        scheduleId: scheduleId,
    });
    const reqOpts = ambassadorWixBookingsAvailabilityV1SlotAvailability.getScheduleAvailability(payload);
    sideEffects?.onSiteCall?.();
    try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return renameKeysFromRESTResponseToSDKResponse(result.data);
    }
    catch (err) {
        const transformedError = sdkTransformError(err, {
            spreadPathsToArguments: {},
            explicitPathsToArguments: { scheduleId: '$[0]' },
            singleArgumentUnchanged: false,
        }, ['scheduleId']);
        sideEffects?.onError?.(err);
        throw transformedError;
    }
}
/** @public
 * @documentationMaturity preview
 * @permissionId BOOKINGS.AVAILABILITY_READ_MULTI_SLOT
 * @permissionScope Read Bookings - Public Data
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-PUBLIC
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Read Bookings - Including Participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-SENSITIVE
 * @permissionScope Read Bookings - all read permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.READ-BOOKINGS
 * @permissionScope Read Bookings Calendar Availability
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-CALENDAR
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @applicableIdentity APP
 * @applicableIdentity VISITOR
 */
export async function calculateMultiSlotAvailability(options) {
    // @ts-ignore
    const { httpClient, sideEffects } = arguments[1];
    const payload = renameKeysFromSDKRequestToRESTRequest({
        from: options?.from,
        to: options?.to,
        timeZone: options?.timeZone,
        bookable: options?.bookable,
        bookingPolicyViolations: options?.bookingPolicyViolations,
        location: options?.location,
        slots: options?.slots,
        slotsPerDay: options?.slotsPerDay,
        cursorPaging: options?.cursorPaging,
    });
    const reqOpts = ambassadorWixBookingsAvailabilityV1SlotAvailability.calculateMultiSlotAvailability(payload);
    sideEffects?.onSiteCall?.();
    try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return renameKeysFromRESTResponseToSDKResponse(result.data);
    }
    catch (err) {
        const transformedError = sdkTransformError(err, {
            spreadPathsToArguments: {},
            explicitPathsToArguments: {
                from: '$[0].from',
                to: '$[0].to',
                timeZone: '$[0].timeZone',
                bookable: '$[0].bookable',
                bookingPolicyViolations: '$[0].bookingPolicyViolations',
                location: '$[0].location',
                slots: '$[0].slots',
                slotsPerDay: '$[0].slotsPerDay',
                cursorPaging: '$[0].cursorPaging',
            },
            singleArgumentUnchanged: false,
        }, ['options']);
        sideEffects?.onError?.(err);
        throw transformedError;
    }
}
//# sourceMappingURL=bookings-availability-v1-slot-availability-availability-calendar.universal.js.map